import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { useCursorList } from '../../../../shared/table-builder/use-cursor-list'
import { useGridSelection } from '../../../../shared/table-builder/grid-selection'
import { ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuDelete, MenuItem, MenuRecycle } from '../../../../shared/menu/menu'
import { IconActivityLog, IconCheck, IconDelete } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { plural } from '../../../../shared/sso-workspace/format'
import { useAuditCapabilities } from '../../../../shared/audit/use-audit-capabilities'
import { useRoleSysAiToolsTelemetry } from '../../../../role/hook/role-sys'
import { aiToolsAvailable } from './ai-tools-api'
import { kindItems, kindNoun, matchFilter } from './ai-tools-words'
import { MarkDialog, markedText } from './mark-dialog'
import { MarkMatchingDialog } from './mark-matching-dialog'
import { DeleteDialog, movedText } from './delete-dialog'
import '../../org/api-keys/api-keys.scss'
import './ai-tools.scss'

// The staff Audit log, narrowed to AI tools telemetry: every ai_tools.*
// event (consent answers, unlinks, marks, and the recycle bin's moves,
// restores and permanent deletes).
export const AUDIT_LOG_URL = '/admin/audit-log?action=ai_tools.*'

// What the Sessions, Postmortems and Findings tabs and a session's records
// share (contract §4.6, §8; R2): a list paged by ai's cursor, filtered above
// the grid and searched in it (the grid's own box, sent as q), whose rows a
// staff member can Mark (SYS_AI_TOOLS_TELEMETRY update) and move to the
// recycle bin (delete), one at a time or in the shared select mode. Each tab
// keeps its own filters, columns, link targets and queryKey; this holds the
// machinery that must behave the same in all of them, so a fix is made once.
//
// useMarkableList({ fetchPage, toRow, filters, failedText, kind, labelOf,
//   consumers, onMarked, onNeedConsumers, orderField, blocked, matchOf,
//   writable, recycleTab }) -> list, for <MarkableList>:
//   fetchPage, toRow, filters, failedText, orderField, blocked: the list's
//     (shared/table-builder/use-cursor-list.js); filters: listFilters(...),
//     memoised by the tab.
//   kind / labelOf(row): what a Mark or a Delete is about ('session',
//     'event', 'postmortem', 'finding') and how a dialog and a row's box
//     name the row.
//   matchOf(filters, search): the selection's filter (matchFilter's form);
//     by default matchFilter(kind, filters + the search).
//   consumers: the consumers that have marked anything (the page reads them
//     once, useConsumers). onMarked(): a mark was saved, so the page reads the
//     consumers again (a new name then joins the filter and the dialog).
//     onNeedConsumers(): optional; the Mark dialog was opened with no
//     consumer known (their first read failed or found none), so the page may
//     read them again.
//   writable: false where nothing may be changed (a binned session's
//     records); recycleTab: the recycle bin's tab for this kind, for the ⋮'s
//     "Recycle bin".
//   list.change(set) wraps a filter's setter: it also clears a failed read's
//     notice, so the new filter's list is asked for. list.available is false
//     in a build that doesn't know the ai service: nothing is asked then.
//
// A row's menu: Delete (the inline trash icon, with delete) and Mark… (in
// its ⋮, with update). Select mode (shared/table-builder/grid-selection.js):
// "Select" in the card's menu while either is allowed; then Mark selected…
// (ids, 500 a call, or all matching except, through the filter form) and
// Delete selected, and in the ⋮ Delete all matching… (with an upTo
// snapshot). After any of these writes the list reloads from the top, select
// mode ends, and the card's status line says what happened (list.notice).
// A single row's mark only refreshes the loaded blocks, so the list keeps
// its place (unless the list is filtered by status, which the mark may take
// the row out of).
//
// list.total is how many rows the filters match once the grid has read to
// the end of the list: Download and Mark all matching then name it
// ("Download 2 sessions"), and select mode counts "all matching except".
// list.personOf(loginId): a person the loaded rows named (the person chip).
export const useMarkableList = ({ fetchPage, toRow, filters, failedText, kind, labelOf, consumers, onMarked, onNeedConsumers, orderField, blocked, matchOf, writable = true, recycleTab }) => {
  const role = useRoleSysAiToolsTelemetry()
  const caps = useAuditCapabilities()
  const canMark = !!role.accessUpdate && writable
  const canDelete = !!role.accessDelete && writable
  const available = aiToolsAvailable()
  const [people, setPeople] = useState({})
  const seen = useRef({})
  // the people the rows name, for the person chip's words
  const toRowSeen = useCallback(item => {
    const row = toRow(item)
    const person = row && row.person
    if (person && person.loginId && !seen.current[person.loginId]) {
      seen.current[person.loginId] = person
      setPeople(known => ({ ...known, [person.loginId]: person }))
    }
    return row
  }, [toRow])
  const cursor = useCursorList({ fetchPage, toRow: toRowSeen, filters, failedText, orderField, blocked })
  const { total, search, reloadFromTop, refreshInPlace } = cursor
  const selection = useGridSelection({ total, labelOf })
  const [marking, setMarking] = useState(null)
  const [markingMatch, setMarkingMatch] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [said, setSaid] = useState(null) // { text, failure }

  // the selection's filter: the list's, with its search
  const latest = useRef()
  latest.current = { kind, filters, search, matchOf }
  const match = useCallback(() => {
    const now = latest.current
    return now.matchOf ? now.matchOf(now.filters, now.search) : matchFilter(now.kind, { ...now.filters, q: now.search })
  }, [])
  // what select mode has chosen: "all matching" is its moment (upTo)
  const chosen = () => selection.request({ ...match(), ...(selection.since ? { upTo: selection.since } : {}) })

  // A write that may move many rows (select mode, all matching): the list
  // starts again at the top, select mode ends, and the line says what it did.
  const wrote = useCallback((text, { marks = false, failure = null } = {}) => {
    setSaid({ text, failure })
    selection.done()
    reloadFromTop()
    if (marks && onMarked) onMarked()
  }, [selection, reloadFromTop, onMarked])
  const say = useCallback(text => setSaid(text ? { text, failure: null } : null), [])

  const askMark = useCallback(row => {
    if (onNeedConsumers && !(consumers && consumers.length)) onNeedConsumers()
    setSaid(null)
    setMarking({ kind, id: row.id, label: labelOf(row) })
  }, [kind, labelOf, consumers, onNeedConsumers])
  const askDelete = useCallback(row => { setSaid(null); setDeleting({ selection: { ids: [row.id] } }) }, [])
  const menu = useMemo(() => row => (
    <Menu small>
      <MenuDelete show={canDelete} onClick={() => askDelete(row)} />
      <MenuItem Icon={IconCheck} text="Mark" show={canMark} onClick={() => askMark(row)} />
    </Menu>
  ), [canMark, canDelete, askMark, askDelete])

  const markSelected = () => {
    if (onNeedConsumers && !(consumers && consumers.length)) onNeedConsumers()
    setSaid(null)
    const asked = chosen()
    if (asked.ids) setMarking({ kind, ids: asked.ids, label: plural(asked.ids.length, kindNoun(kind), kindItems(kind)) })
    else setMarkingMatch({ filter: asked.filter, except: asked.except || [], count: selection.count })
  }
  const selectActions = [
    <MenuItem key="mark-selected" Icon={IconCheck} text="Mark selected" show={canMark} disabled={selection.none} onClick={markSelected} />,
    <MenuItem key="delete-selected" Icon={IconDelete} text="Delete selected" show={canDelete} disabled={selection.none} onClick={() => { setSaid(null); setDeleting({ selection: chosen() }) }} />
  ]
  // the ⋮'s own: what goes with all matching, and where these rows go
  const menuItems = [
    <MenuItem key="delete-all" Icon={IconDelete} text="Delete all matching" pin={false} show={canDelete} disabled={!!blocked || total === 0}
      onClick={() => { setSaid(null); setDeleting({ selection: { filter: { ...match(), upTo: new Date().toISOString() } } }) }} />,
    <MenuRecycle key="recycle" show={canDelete && !!recycleTab} to={`/admin/ai-tools/recycle?tab=${recycleTab}`} />,
    <MenuItem key="audit-log" Icon={IconActivityLog} text="audit log" show={!!caps.sysAuditLog} to={AUDIT_LOG_URL} />
  ]

  // A save that finishes after its dialog was cancelled (and maybe another
  // opened) still counts: the grid reloads and the page hears of it, but it
  // closes only its own dialog.
  const saved = (target, answer) => {
    setMarking(current => (current === target ? null : current))
    if (target.ids) return wrote(markedText(kind, answer), { marks: true, failure: answer && answer.partial ? answer.partial.failure : null })
    // A mark can drop the row from a status-filtered list: the pager's block
    // cursors would then be stale, so that list starts again from the top.
    if (filters.status) reloadFromTop(); else refreshInPlace()
    if (onMarked) onMarked()
  }
  const markedMatching = (target, answer) => {
    setMarkingMatch(current => (current === target ? null : current))
    wrote(markedText(kind, answer), { marks: true })
  }
  const moved = (target, answer) => {
    setDeleting(current => (current === target ? null : current))
    wrote(movedText(kind, answer))
  }

  const notice = said
    ? said.failure
      ? <span className="text-danger" role="alert">{said.text} <ErrorReference error={said.failure} compact /></span>
      : <span className="sw-hint" role="status">{said.text}</span>
    : null
  const dialogs = (
    <>
      {marking && <MarkDialog target={marking} consumers={consumers} onCancel={() => setMarking(null)} onMarked={answer => saved(marking, answer)} />}
      {markingMatch && <MarkMatchingDialog kind={kind} filters={filters} selected={markingMatch} consumers={consumers} onCancel={() => setMarkingMatch(null)} onDone={answer => markedMatching(markingMatch, answer)} />}
      {deleting && <DeleteDialog kind={kind} selection={deleting.selection} onCancel={() => setDeleting(null)} onDone={answer => moved(deleting, answer)} />}
    </>
  )

  return {
    ...cursor,
    available,
    canMark,
    canDelete,
    selectable: canMark || canDelete,
    selection,
    selectActions,
    menuItems,
    menu,
    consumers,
    wrote,
    say,
    said: said && said.text,
    notice,
    dialogs,
    personOf: loginId => people[loginId] || null
  }
}

// The tab's body, the house list page (owner 2026-10-23): its filters
// (controls, already wired to list.change) in one compact toolbar row
// (.sw-toolbar), then the list's card, whose header is the shared menu
// (owner 2026-10-29): actions ({ items, notice, dialog }, useMatchingActions)
// gives the tab's own items -- Download pinned as a button, Mark all
// matching… in the ⋮ -- before the list's (Select, and in the ⋮ Delete all
// matching…, Recycle bin, Audit log, Table settings), and the status line
// under it (the tab's, else the list's); in the card the grid (the tab's
// columns as children, the row menu after them; its search box sends q) or,
// in its place, a failed read's notice with Try again or a date range that
// can't be asked; and the dialogs. A click on a
// row opens rowTarget(row) (a page), or, for a list whose rows open in place,
// calls onRowOpen(row) instead (in select mode it ticks the row instead); an
// infinite grid also raises a click for a row whose block has not loaded
// yet, with no data, which opens nothing.
// queryKey keeps this grid's scroll, sort and search in the URL apart from
// the other grids'.
//
// The lists have two-line rows; the three tabs' are newest first, in a card of
// their own. A list inside a section of its own (a session's records, oldest
// first) passes subtitle and flush (no card of its own); rowHeight null is
// the theme's one-line rows.
export const MarkableList = ({ list, queryKey, title, subtitle = 'Newest first', flush, rowHeight = ROW_HEIGHT_TWO_LINE, controls, actions, emptyState, rowTarget, onRowOpen, children }) => {
  const history = useHistory()
  const tableRef = useRef()

  if (!list.available) return <Notice tone="warn">AI tools telemetry isn't available in this build.</Notice>

  return (
    <>
      {controls && <div className="sw-toolbar ai-toolbar" role="group" aria-label={`Filter the ${String(title || 'list').toLowerCase()}`}>{controls}</div>}

      {/* The card stays whatever the list's state: its header holds the list's
          actions, and a running Download (or Mark all matching's dialog, and
          what they last said) must not be lost when a read fails or the
          dates go wrong. Only the grid gives way to the notice. */}
      <FleetGrid title={title} subtitle={subtitle} tableRef={tableRef} rowHeight={rowHeight} flush={flush} tableSettings={false}
        menu={[...((actions && actions.items) || []), ...list.menuItems]} notice={(actions && actions.notice) || list.notice}
        selection={list.selectable && !list.error && !list.blocked ? list.selection : null} selectActions={list.selectActions}>
        {list.error
          ? <Notice tone="bad" role="alert">{list.error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={list.retry}>Try again</button><ErrorReference error={list.failure} /></Notice>
          : list.blocked
            ? <Notice tone="bad" role="alert">{list.blocked}</Notice>
            : <TableAgInfinite ref={tableRef} queryKey={queryKey} onGetRows={list.getRows} reloadKey={list.reloadKey} refreshKey={list.refreshKey} rowHeight={rowHeight}
              selection={list.selection}
              onRowClicked={row => row && (onRowOpen ? onRowOpen(row) : history.push(rowTarget(row)))}
              emptyState={emptyState}>
              {children}
              <ColumnMenu menu={list.menu} />
            </TableAgInfinite>}
      </FleetGrid>

      {actions && actions.dialog}
      {list.dialogs}
    </>
  )
}
